import { Button } from "@/components/ui/button";
import { Printer, ArrowRight, Share2, Lock, StepForward } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect } from "react";

interface StoryHeaderProps {
  onPrint: (viewMode: "paginated" | "entire") => void;
  onContinueStory: () => void;
  isFinished: boolean;
  hasNoContinuation: boolean;
  isCreator: boolean;
  finished_at: string | null;
  canContinue: boolean;
  canShare: boolean;
  onShareStory: () => void;
  viewMode: "paginated" | "entire"; // Add this prop
  onViewModeChange: (mode: "paginated" | "entire") => void; // Add this callback
}

const StoryHeader = ({ 
  onPrint, 
  onContinueStory, 
  isFinished, 
  hasNoContinuation,
  isCreator,
  finished_at,
  canContinue,
  canShare,
  onShareStory,
  viewMode, // Get viewMode from props
  onViewModeChange // Get callback from props
}: StoryHeaderProps) => {
  const { t } = useLanguage();

  return (
    <div className="mt-6 w-full">
      <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4 mb-6 w-full">
        <Button
          onClick={() => onPrint(viewMode)} // Passing viewMode but we'll always print entire
          className="bg-[#7E69AB] text-white hover:bg-[#6E59A5] w-full"
        >
          <Printer className="w-4 h-4 mr-2" />
          {t('story.print')}
        </Button> 
        {finished_at && isCreator && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  type="button"
                  variant={!canShare ? "locked" : "outline"}
                  onClick={onShareStory}
                  className="gap-2 relative w-full"
                >
                  <Share2 className="h-4 w-4" />
                  {t('story.share')}
                  {!canShare && (
                    <Lock className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              {!canShare && (
                <TooltipContent>
                  <p>{t('story.tooltips.upgradeToShare')}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}

        {/* removed hasNoContinuation && */}
        {isCreator && isFinished &&  (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  type="button"
                  variant={!canContinue ? "locked" : "outline"}
                  onClick={onContinueStory}
                  className="gap-2 relative w-full"
                >
                  <StepForward className="h-4 w-4" />
                  {t('story.continueStory')}
                  {!canContinue && (
                    <Lock className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              {!canContinue && (
                <TooltipContent>
                  <p>{t('story.tooltips.upgradeToContinue')}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
        
        <div className="flex items-center gap-2">
          <Switch
            checked={viewMode === "entire"}
            onCheckedChange={(checked) => onViewModeChange(checked ? "entire" : "paginated")}
          />
          <span>{viewMode === "entire" ? t('story.viewEntire') : t('story.viewPaginated')}</span>
        </div>
      </div>
    </div>
  );
};

export default StoryHeader;
